import React, { Component, ReactNode } from 'react';
import { TransactionData, TransactionRiskType } from '../../../../domain/stream/transaction/TransactionDomains';
import { InstanceContainer } from '../../../../config/binding/InstanceBinding';
import Injectables from '../../../../constant/Injectables';
import _ from 'lodash';
import { query, repository } from '@trlogic/client-js';
import CollectionNames from '../../../../constant/CollectionNames';
import { RuleJsonModel } from '../../../../domain/collection/rule/FlowDomains';
import WorkflowNodeUtil from '../../../../util/WorkflowNodeUtil';
import { EventToken } from '../../../../domain/collection/rule/RuleDomains';
import { Tooltip } from 'primereact/tooltip';
import Icon from '../../../../icons/Icon';
import { FormicaProps, withFormica } from '../../../../helpers/withFormica';
import QueryBuilder = query.QueryBuilder;
import eq = query.eq;
import ComponentName from '../../../../constant/ComponentName';
import LanguageUtil from '../../../../util/LanguageUtil';

class CaseDetailSummaryPanelComponent extends Component<Props, State> {
  public state: State = new State();

  private repository: repository.ReadOnlyQueryableRepository = InstanceContainer.get(Injectables.REPOSITORY);

  public render(): ReactNode {
    const { ruleInfo } = this.state;

    return (
      <div
        style={{ boxShadow: '2px 2px 15px 0px #0000000D' }}
        className={'flex w-full flex-col bg-[#FFFFFF] border-[#E0E4E8] border-[1px] rounded-[10px] max-h-[416px] h-[416px] overflow-y-scroll'}
      >
        <span className='flex flex-row h-[50px] w-full  border-b-[1px] border-[#E0E4E8] rounded-t-[10px] bg-[#F9FAFB]'>
          <span className={'text-primary  2xl m-[15px]'}>{LanguageUtil.get({ id: 'common.label.ruleSummary', defaultMessage: 'Rule Summary' })}</span>
        </span>
        {Object.values(ruleInfo)?.length <= 0 ? (
          <div className={'flex flex-col items-center justify-center w-full min-h-[364px] bg-white gap-5 px-5 py-5 rounded-b-[10px]'}>
            <div className={'flex items-center w-40 h-40 justify-center rounded-full bg-secondary'}>
              <section className={'flex items-center w-32 h-32 justify-center rounded-full bg-white drop-shadow'}>
                <Icon icon={'sidebar-rule'} className={'text-[#728EFF]'} size={45} />
              </section>
            </div>
            <section className={'flex flex-col items-center justify-center gap-y-2 select-none'}>
              <div className={'text-f-black-2 2xl'}>
                {LanguageUtil.get({ id: 'pages.case.detail.header.noRulesCreatedYet' })}
                <strong className={'cursor-pointer'} onClick={this.goRuleCreate}>
                  {LanguageUtil.get({ id: 'pages.case.detail.header.click' })}
                </strong>
                {LanguageUtil.get({ id: 'pages.case.detail.header.toCreate' })}
              </div>
            </section>
          </div>
        ) : (
          <div className={'h-full p-[10px] overflow-y-scroll'}>
            <div className={'alarm-summary-label'}>
              <span className={'flex w-full text-f-black-2'}>{LanguageUtil.get({ id: 'common.label.rule', description: 'Rule' })}</span>
              <span className={'flex w-full text-f-black-2'}>{LanguageUtil.get({ id: 'common.label.description', description: 'Description' })}</span>
              <span className={'flex text-f-black-2'}>{LanguageUtil.get({ id: 'common.label.score', description: 'Score' })}</span>
            </div>
            {Object.values(ruleInfo)
              .sort((a, b) => b.score - a.score)
              .map((value: { description: string; score: number; name: string }, index: number) => {
                return (
                  <div className={'flex items-center justify-center w-full text-f-black-3 shrink-0  px-5 border-b h-10 box-border'} key={index}>
                    <div className={'flex w-full items-center'} data-testid={`${ComponentName.CASE}_detail_rule_summary_${value.name}_name_text`}>
                      {value.name}
                    </div>
                    <div className={'flex w-full items-center'}>
                      <p
                        key={index + 'description'}
                        className={'rule-description' + index}
                        data-testid={`${ComponentName.CASE}_detail_rule_summary_${value.name}_description_text`}
                      >
                        {value.description.length > 50 ? value.description.slice(0, 50) + '...' : value.description}
                      </p>
                      <Tooltip target={`.rule-description${index}`} position='top' className={'p-tooltip'}>
                        {' '}
                        {value.description}{' '}
                      </Tooltip>
                    </div>
                    <div
                      className={`${this.getLabelColorFromScore(value.score)} flex  items-center justify-center`}
                      data-testid={`${ComponentName.CASE}_detail_rule_summary_${value.name}_score_text`}
                    >
                      {value.score}
                    </div>
                  </div>
                );
              })}
          </div>
        )}
      </div>
    );
  }

  public componentDidMount(): void {
    this.buildRuleDescription();
  }

  public componentDidUpdate(prevProps: Readonly<Props>) {
    if (!_.isEqual(this.props.transactionData, prevProps.transactionData)) {
      this.buildRuleDescription();
    }
  }

  private buildRuleDescription = async () => {
    const { transactionData, riskType } = this.props;
    if (transactionData == undefined) return;
    const ruleQuery = QueryBuilder.select().from(CollectionNames.RULES).where(eq('riskType', riskType)).build();
    const rules: RuleJsonModel[] = await this.repository.findMany(ruleQuery);
    const ruleInfo: Record<string, { description: string; score: number; name: string }> = {};
    rules
      .filter((r) => transactionData.name == (WorkflowNodeUtil.getTransaction(r.workflow)?.value as EventToken).name)
      .forEach((r) => {
        const score = transactionData.result[riskType].rules.find((rule) => rule.name == r.name);
        ruleInfo[r.name] = { description: r.description || '', score: score?.score || 0, name: r.name };
      });
    this.setState({ ruleInfo });
  };

  private goRuleCreate = () => {
    const { router } = this.props;
    router.navigate(`/rule/create`);
  };

  private getLabelColorFromScore(score: number = 0) {
    if (score == 0) {
      return 'text-[#7ECC8B]';
    } else if (score > 0 && score < 100) {
      return 'text-[#F19E46]';
    } else if (score == 100) {
      return 'text-[#D23531]';
    }
  }
}

interface Props extends FormicaProps {
  transactionData: TransactionData | undefined;
  riskType: TransactionRiskType;
}

class State {
  ruleInfo: Record<string, { description: string; score: number; name: string }> = {};
}

export default withFormica(CaseDetailSummaryPanelComponent);
